import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import {
  Input,
  Form,
  Space,
  Button,
  Drawer,
  Select,
  Divider,
  DatePicker
} from '@lockerpm/design';
import dayjs from 'dayjs';
import {
  PlusOutlined
} from '@ant-design/icons';

import commonComponents from '../../../../../components/common';
import EnvironmentFormData from "../environments/FormData";

import secretServices from '../../../../../services/secret';

import global from '../../../../../config/global';
import storeActions from '../../../../../store/actions';

function FormData(props) {
  const { OnboardingTooltip } = commonComponents;
  const {
    visible = false,
    item = null,
    allItems = [],
    environments = [],
    leakedSecret = null,
    fetchEnvironments = () => { },
    onClose = () => { },
    onReload = () => { }
  } = props
  const { t } = useTranslation()
  const selectedProject = useSelector((state) => state.project.selectedProject);

  const [form] = Form.useForm()
  const [callingAPI, setCallingAPI] = useState(false);
  const [formVisible, setFormVisible] = useState(false);

  useEffect(() => {
    if (visible) {
      if (item) {
        form.setFieldsValue({
          key: item.secret.key,
          value: item.secret.value,
          description: item.secret.description,
          environmentId: item.environmentId || "",
          expiredDate: (item.expired_date || item.expiredDate)
            ? dayjs(Number(item.expired_date || item.expiredDate) > 10000000000
              ? Number(item.expired_date || item.expiredDate)
              : Number(item.expired_date || item.expiredDate) * 1000)
            : null
        })
      } else {
        form.setFieldsValue({
          key: leakedSecret ? leakedSecret.leakedSecret?.key || '' : '',
          value: leakedSecret ? leakedSecret.leakedSecret?.value || '' : '',
          description: '',
          environmentId: "",
          expiredDate: null
        })
      }
    }
  }, [visible, item, leakedSecret])


  const handleSave = async () => {
    form.validateFields().then(async (value) => {
      const secret = await secretServices.get_payload(value, selectedProject.id)
      let request = null
      setCallingAPI(true);
      if (item) {
        request = secretServices.update(selectedProject.id, item.id, secret)
      } else {
        request = secretServices.create(selectedProject.id, secret)
      }
      await request?.then(() => {
        onReload();
        onClose();
        if (!item) {
          global.store.dispatch(storeActions.updateIsFetchProjects(true));
          global.store.dispatch(storeActions.updateChangeProcessKey('step_3'));
        }
      }).catch((error) => {
        global.pushError(error)
      })
      setCallingAPI(false);
    })
  }

  return (
    <div className={props.className}>
      <Drawer
        title={item ? t('secrets.edit') : t('secrets.new')}
        placement="right"
        onClose={onClose}
        open={visible}
        footer={
          <Space className='flex items-center justify-end'>
            <Button
              disabled={callingAPI}
              onClick={onClose}
            >
              {t('button.cancel')}
            </Button>
            <Button
              type="primary"
              loading={callingAPI}
              onClick={handleSave}
            >
              {item ? t('button.save') : t('button.create')}
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          labelAlign={'left'}
        >
          <Form.Item
            name={'key'}
            className='mb-4'
            label={<div>
              <p className='font-bold'>{t('secret.key')}</p>
              <span>{t('secret.key_desc')}</span>
            </div>}
            rules={[
              global.rules.REQUIRED(t("secret.key")),
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const envSecrets = allItems.filter((i) => i.id !== item?.id && i.environmentId == getFieldValue('environmentId'))
                  if (value && envSecrets.map((i) => i.secret.key?.toLowerCase()).includes(value?.toLowerCase())) {
                    return Promise.reject(new Error(t('validation.key_existed')));
                  }
                  return Promise.resolve();
                },
              })
            ]}
          >
            <Input
              size="medium"
              disabled={callingAPI}
              className='w-full'
              placeholder={t('placeholder.enter')}
            />
          </Form.Item>
          <Form.Item
            name={'value'}
            label={<div>
              <p className='font-bold'>{t('secret.value')}</p>
              <span>{t('secret.value_desc')}</span>
            </div>}
            rules={[
              global.rules.REQUIRED(t("secret.value"))
            ]}
          >
            <Input.TextArea
              disabled={callingAPI}
              className='w-full'
              autoSize={true}
              placeholder={t('placeholder.enter')}
            />
          </Form.Item>
          <Form.Item
            name={'description'}
            label={<div>
              <p className='font-bold'>{t('common.description')}</p>
            </div>}
            rules={[]}
          >
            <Input.TextArea
              disabled={callingAPI}
              className='w-full'
              autoSize={true}
              placeholder={t('placeholder.description')}
            />
          </Form.Item>
          <Form.Item
            name={'expiredDate'}
            label={<div>
              <p className='font-bold'>{t('secret.expired_date')}</p>
              <span>{t('secret.expired_date_desc')}</span>
            </div>}
          >
            <DatePicker
              format="DD-MM-YYYY"
              className="w-full"
              placeholder={t('placeholder.date')}
              disabled={callingAPI}
            />
          </Form.Item>
          <Form.Item
            name={'environmentId'}
            label={<div>
              <p className='font-bold'>{t('secret.environment')}</p>
              <span>{t('secret.environment_desc')}</span>
            </div>}
          >
            <Select
              placeholder={t('placeholder.select')}
              className="w-full"
              disabled={callingAPI}
              dropdownRender={(menu) => (<>
                {menu}
                <Divider style={{ margin: '4px 0' }} />
                <div
                  className='text-primary cursor-pointer flex items-center px-2 py-1'
                  onClick={() => setFormVisible(true)}
                >
                  <PlusOutlined />
                  <p className='ml-2'>{t('button.new_environment')}</p>
                </div>
              </>)}
            >
              <Select.Option
                value={""}
              >
                <OnboardingTooltip
                  title={t('onboarding.env_all_default')}
                  placement="left"
                  content={
                    <div className='w-full'>
                      {t('common.all_default')}
                    </div>
                  }
                />
              </Select.Option>
              {environments.map((env) => (
                <Select.Option
                  value={env.id}
                  key={env.id}
                >
                  {env.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Drawer>
      <EnvironmentFormData
        visible={formVisible}
        item={null}
        onClose={() => setFormVisible(false)}
        onReload={() => fetchEnvironments()}
        callback={(env) => form.setFieldValue('environmentId', env.id)}
      />
    </div>
  );
}

export default FormData;
